import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  memo,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import {
  Link,
  NavLink,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { decrypt } from "../../../../actions/common";
import cleanNestedObject from "../../../../utils/cleanObject";
import { apiRequest } from "../../../../utils/utils";
import CustomFilter from "./CustomFilter";
import CtaOptions from "../../customer/CtaOptions";
import StatusBarPagination from "../../../StatusBarPagination";
import {
  maskEmail,
  checkCrmPermission,
  getCookie,
} from "../../../common/Utility";
import Checkbox from "rsuite/esm/Checkbox";

import LoginToAccount from "./LoginToAccount";
import DealsActionColumn from "../../deals/components/DealsActionColumn";
import ContactsActionColumn from "./ContactsActionColumn";
import RaiseCustomInvoiceNew from "../../deals/components/RaiseCustomInvoice";
import MarkAsPaidNew from "../../deals/components/MarkAsPaid";
import { format } from "date-fns";

const AgGridComponent = forwardRef(
  (
    {
      agData = {},
      storePermission = null,
      ctaModal,
      getAgGridData,
      hasChildRows = false,
      columnsDef,
      callfunctionapi,
      productData,
      selection = false,
      onSelectionChange,
      userList,
      refetchAgGrid,
      ...rest
    },
    ref,
  ) => {
    const [tooltipData, setTooltipData] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef(null);
    const internalGridRef = useRef(null);
    const gridRef = internalGridRef;
    const { attribute_set_key } = useParams();

    useImperativeHandle(ref, () => ({
      get api() {
        return internalGridRef.current ? internalGridRef.current.api : null;
      },
      get columnApi() {
        return internalGridRef.current
          ? internalGridRef.current.columnApi
          : null;
      },
    }));

    let menuButton = useRef(null);
    const timeoutRef = useRef(null);
    const [rowData, setRowData] = useState([]);
    const [expandedRows, setExpandedRows] = useState(new Set());

    const [actionColData, setActionColData] = useState({
      // deals only
      raiseCustomInvoiceModalData: {},
      markAsPaidModalData: {},
      confirmModalData: {},
      // contacts
      LoginToAccount: {},
      openModal: "",
    });

    const prevRequestRef = useRef(null);
    const prevRes = useRef(null);
    const toggleRowExpansion = (customerId, node) => {
      if (!hasChildRows) return; // Do nothing if there are no child rows
      if (!gridRef.current || !gridRef.current.api) return;

      if (node.expanded) {
        gridRef.current.api.setRowNodeExpanded(node, false);
      } else {
        gridRef.current.api.setRowNodeExpanded(node, true);
      }

      // Update our state to track expand button display
      setExpandedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(customerId)) {
          newSet.delete(customerId);
        } else {
          newSet.add(customerId);
        }
        return newSet;
      });
    };

    let user_uuid = getCookie("muvi_useruuid");

    var has_local_data = localStorage.getItem("encdata") ? 1 : 0;
    var localdata = has_local_data
      ? decrypt(localStorage.getItem("encdata"), 0)
      : "";

    const handleIconClick = (event, rowData) => {
      const { pageX, pageY } = event;

      const sidebar = document.querySelector(".left_col");

      setTooltipPosition({
        top: pageY - 90,
        left: pageX - sidebar.offsetWidth - 270,
      });
      setTooltipData(rowData);
    };

    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setTooltipData(null);
      }
    };

    const datasourceRef = useRef(null);
    const firstrun = useRef(true);
    const debounceRef = useRef(null);

    useEffect(() => {
      const { syncFilterWithAggrid, search } = agData;

      if (!syncFilterWithAggrid && !search) return;
      if (!gridRef.current || !gridRef.current.api) return;

      const updatedData = { ...syncFilterWithAggrid };
      const searchValue = search;

      if (firstrun.current) {
        firstrun.current = false;
        return;
      }

      const signature = JSON.stringify({
        filters: updatedData,
        search: searchValue,
      });

      if (datasourceRef.current === signature) return;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        datasourceRef.current = signature;

        gridRef.current.api.setServerSideDatasource(
          createServerSideDatasource(updatedData, searchValue),
        );
      }, 900);

      return () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
      };
    }, [agData.syncFilterWithAggrid, agData.search]);

    // Memoized detail columns fetching - only happens once
    const detailColumns = useMemo(() => {
      const fetchDetailColumns = async () => {
        if (!rest.isLeadsDetailsPage) {
          return;
        }

        try {
          const response = await fetch(
            process.env.REACT_APP_CRM + "/crm/get-columns-list",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                type: ["crm_product", "crm_ask_sales", "crm_live_chat"],
              }),
            },
          ).then((res) => res.json());

          const columnsData = response.data || {};
          const crm_product = columnsData.crm_product || [];
          const crm_ask_sales = columnsData.crm_ask_sales || [];
          const crm_live_chat = columnsData.crm_live_chat || [];

          return {
            productColumns:
              crm_product.map((col) => ({
                field: col.crm_field,
                headerName: col.header_name,
                options: col.options || [],
                attribute_type: col.attribute_type,
                width: 200,
                cellRenderer: getCellRenderer(
                  col.crm_field,
                  col.attribute_type,
                ),
              })) || [],

            messagesColumns:
              crm_ask_sales.map((col) => ({
                field: col.crm_field,
                headerName: col.header_name,
                options: col.options || [],
                attribute_type: col.attribute_type,
                width: 200,
                cellRenderer: getCellRenderer(
                  col.crm_field,
                  col.attribute_type,
                ),
              })) || [],

            chatColumns:
              crm_live_chat.map((col) => ({
                field: col.crm_field,
                headerName: col.header_name,
                options: col.options || [],
                attribute_type: col.attribute_type,
                width: 200,
                cellRenderer: getCellRenderer(
                  col.crm_field,
                  col.attribute_type,
                ),
              })) || [],
          };
        } catch (error) {
          console.error("Error fetching detail columns:", error);
          return {
            productColumns: [],
            messagesColumns: [],
            chatColumns: [],
          };
        }
      };

      // return fetchDetailColumns();
    }, []);

    const onSortRequested = (params) => {
      const { colId } = params.column;
      if (params.column.sort == "undefined") {
        params.setSort("asc");
      } else if (params.column.sort === "desc") {
        params.setSort("asc");
      } else {
        params.setSort("desc");
      }
    };

    const createServerSideDatasource = (
      filters = null,
      search = null,
      preProcessedData = null,
    ) => ({
      getRows: async (params) => {
        // 1. If expansion row data is passed → skip API
        if (preProcessedData) {
          params.successCallback(preProcessedData, preProcessedData.length);
          return;
        }

        const { app_token } = agData;

        // Sorting
        let sortColumn = agData.sort;
        let sortOrder = agData.order;

        if (params.request.sortModel.length > 0) {
          const { colId, sort } = params.request.sortModel[0];
          sortColumn = colId;
          sortOrder = sort;
        }

        // === Get page size safely ===
        const perPage = gridRef.current.api.paginationProxy.pageSize || 20;

        // Correct page calculation
        const currentPage = params.request.startRow / perPage + 1;

        // 2. Build request payload
        let requestdata = {
          app_token,
          attribute_set_key: agData.attribute_set_key || "crm",
          user_uuid: user_uuid || localdata.user_uuid,
          filter: filters,
          current_page: currentPage,
          sort: sortColumn,
          order: sortOrder,
          search: search || "",
          per_page: perPage,
        };

        const cleaned = cleanNestedObject(requestdata);
        const signature = JSON.stringify(cleaned);

        // ------------------------------------------------------------------
        // 4. Main API handler with caching
        // ------------------------------------------------------------------
        const callingapi = async () => {
          let checkencrypt = rest.hasOwnProperty("encrypt")
            ? rest.encrypt
            : true;

          // Safe loading overlay
          if (gridRef.current.api) {
            gridRef.current.api.showLoadingOverlay();
          }

          let resdata = null;

          // MATCH → RETURN CACHED RESPONSE
          if (prevRequestRef.current === signature) {
            resdata = prevRes.current;
          } else {
            // NEW PAYLOAD → API CALL
            resdata = await apiRequest(
              process.env.REACT_APP_CRM_ADMIN +
                `/api/crm/${attribute_set_key}/list`,
              "POST",
              cleaned,
              {},
              true,
            );

            // store new response + new signature
            prevRes.current = resdata;
            prevRequestRef.current = signature;
          }

          // ------------------------------------------------------------------
          // 5. Process API Response
          // ------------------------------------------------------------------
          if (resdata.code === 200) {
            const { data: rdata, total_count } = resdata;

            if (gridRef.current.api) {
              if (!rdata.length) gridRef.current.api.showNoRowsOverlay();
              else gridRef.current.api.hideOverlay();
            }

            // Update total count if needed
            if (rest.getTotalCount) {
              rest.getTotalCount((state) => ({
                ...state,
                isFilterChanges: !state.isFilterChanges,
                totalCount: total_count,
              }));
            }

            setRowData(rdata);
            params.successCallback(rdata, total_count);
          }
        };

        await callingapi(); // IMPORTANT: await to avoid AG Grid race
      },
    });

    const handleProcessCellForClipboard = (params) => {
      const { colDef } = params.column;
      const type = colDef.attribute_type;
      const permission = checkCrmPermission();

      switch (type) {
        case "tse_owner":
        case "owner":
        case "account_manager":
        case "sales_manager": {
          const { options } = colDef;

          if (options && options.data && Array.isArray(options.data)) {
            const val = userList.data.find(
              (item) => (item._id || item.user_uuid) === params.value,
            );
            if (val) {
              return val.option_name || val.name || val.value;
            }
          }
          return params.value;
        }
        case "product": {
          const { options } = colDef;

          if (Array.isArray(options)) {
            const val = options.find(
              (item) => (item._id || item.id) === params.value,
            );
            if (val) {
              return val.option_name || val.name || val.value;
            }
          }
          return params.value;
        }
        case "tel": {
          if (!permission) {
            return "";
          }
          return params.value;
        }
        case "text": {
          if (colDef.field === "email" && !permission) return "";

          if (Array.isArray(params.value)) {
            return params.value.join(", ");
          }
          return params.value;
        }
        case "email": {
          if (!permission) return "";
          return params.value;
        }
        case "toggle": {
          const val = params.value;

          const yesValues = [true, "true", 1, "1", "yes"];
          // const noValues = [false, "false", 0, "0", "no"];

          if (
            yesValues.includes(val) ||
            yesValues.includes(String(val).toLowerCase())
          ) {
            return "Yes";
          }
          return "No";
        }
        case "date": {
          const date = new Date(params.value * 1000);
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
        }
        case "api": {
          const { options } = colDef;

          if (Array.isArray(options)) {
            const val = options.find(
              (item) => (item._id || item.id) === params.value,
            );
            if (val) {
              return val.option_name || val.name || val.value;
            }
          }
          return params.value;
        }
        case "customer_type": {
          const value = params.node.data[params.column.colId];

          return value;
        }

        default:
          return params.value;
          break;
      }
    };

    const onColumnMoved = (ev) => {
      if (ev.source === "uiColumnDragged") {
        const {
          columnApi: {
            columnModel: { displayedTreeCentre },
          },
        } = ev;

        const newrr = [];

        const orderinsequence = {
          app_token: process.env.REACT_APP_tattle_token,
          user_uuid: user_uuid ? user_uuid : localdata.user_uuid,
          attribute_set_key: agData.attribute_set_key
            ? agData.attribute_set_key
            : "crm",
          attributes: newrr,
        };

        displayedTreeCentre.forEach((item, i) => {
          if (item.colDef.field !== undefined) {
            newrr.push(item.colDef.field);
          }
        });

        // Clear the previous timeout if it exists
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(async () => {
          try {
            await fetch(`${process.env.REACT_APP_tattle_api}/sequence`, {
              method: "POST",
              body: JSON.stringify(orderinsequence),
            });
          } catch (err) {
            console.log(err.message);
          }
        }, 200);
      }
    };

    const onColumnResized = (params) => {
      if (params.source === "uiColumnDragged") {
        const columnspropetise = params.column.colDef;
        const formvalues = {
          ...columnspropetise,
          width: params.column.actualWidth || 275,
        };
        const { options, ...newoptions } = formvalues;
        const boardDetails = {
          app_token: process.env.REACT_APP_tattle_token,
          user_uuid: user_uuid ? user_uuid : localdata.user_uuid,
          attribute_set_key: agData.attribute_set_key
            ? agData.attribute_set_key
            : "crm",
          attributes: [formvalues.field],
          width: newoptions.width,
        };

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(async () => {
          try {
            await fetch(
              `${process.env.REACT_APP_tattle_api}/attribute/resize`,
              {
                method: "POST",
                body: JSON.stringify(boardDetails),
              },
            );
          } catch (err) {
            console.log(err.message);
          }
        }, 200);
      } else {
        return;
      }
    };
    // check the updated props

    const getFilterParams = (field, val) => {
      rest.getTotalCount((state) => {
        return {
          ...state,
          aggridData: {
            ...state.aggridData,
            syncFilterWithAggrid: {
              ...state.aggridData.syncFilterWithAggrid,
              ...val,
            },
          },
        };
      });
    };

    useEffect(() => {
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.setServerSideDatasource(
          createServerSideDatasource(agData.syncFilterWithAggrid),
        );
      }
    }, [agData.recallcrmapi, agData.attribute_set_key]);
    const isFirstLoadRef = useRef(true);
    const onGridReady = (params) => {
      gridRef.current = params;
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        return;
      }
      params.api.setServerSideDatasource(
        createServerSideDatasource(agData.syncFilterWithAggrid),
      );

      // getCallFocus();
    };
    const handleCellKeyDown = (event) => {
      const { column, rowIndex, key } = event;
      if (!gridRef.current) return;

      const api = gridRef.current.api;
      const colId = column && column.getColId();

      if (key === "ArrowRight") {
        // Get the next column
        const nextColumn = api.getDisplayedColAfter(colId);
        if (nextColumn) {
          api.ensureColumnVisible(nextColumn.getColId());
          api.setFocusedCell(rowIndex, nextColumn.getColId());
        }
      } else if (key === "ArrowLeft") {
        // Get the previous column
        const prevColumn = api.getDisplayedColBefore(colId);
        if (prevColumn) {
          api.ensureColumnVisible(prevColumn.getColId());
          api.setFocusedCell(rowIndex, prevColumn.getColId());
        }
      }
    };

    const getCallFocus = () => {
      setTimeout(() => {
        // Get all displayed columns
        if (gridRef.current) {
          const allColumns = gridRef.current.columnApi.getAllDisplayedColumns();

          // Find the first non-action column
          const firstCol = allColumns.find(
            (col) => col.getColId() !== "actions",
          );

          if (firstCol) {
            requestAnimationFrame(() => {
              gridRef.current.api.setFocusedCell(0, firstCol.getColId()); // Focus on first row, first column (excluding action column)
            });
          }
        }
      }, 300);
    };

    const onSelectionChanged = useCallback(() => {
      if (!gridRef.current || !gridRef.current.api) return;

      var api = gridRef.current.api;

      // Defer UI-heavy work to avoid lag
      requestAnimationFrame(function () {
        // Refresh custom header checkbox (RSuite)
        api.refreshHeader();

        // Emit selected rows to parent
        if (typeof onSelectionChange === "function") {
          var selectedNodes = api.getSelectedNodes();
          var selectedData = selectedNodes.map(function (node) {
            return node.data;
          });

          onSelectionChange(selectedData);
        }
      });
    }, [onSelectionChange]);

    useEffect(() => {
      if (rowData.length && columnsDef.length) {
        // getCallFocus();
      }
    }, [rowData, columnsDef]);

    const hiddenColumns = async (params) => {
      const { colId } = params.column;
      const data = {
        app_token: agData.app_token,
        attribute_set_key: agData.attribute_set_key,
        user_uuid: user_uuid ? user_uuid : localdata.user_uuid,
        status_type: "hide",
        attributes: [colId],
      };
      const responseData = await apiRequest(
        `${process.env.REACT_APP_tattle_api}/attribute/hide`,
        "POST",
        data,
      );

      if (responseData.code === 200) {
        callfunctionapi();
      }
    };

    const customerHeaderCall = (params) => {
      let sort;
      let fillterjsx;

      if (params.enableSorting) {
        sort = (
          <div className="arrow-chevron">
            <div
              className="sorting-custom"
              onClick={() => onSortRequested(params)}
            >
              <div>
                <span
                  className={`customSortDownLabel sorttt ${
                    params.column.sort === "asc" ? "notdisaplay" : ""
                  } desc${params.column.sort === "desc" ? "descactive" : ""}`}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 11.0833V2.91663"
                      stroke="black"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2.9165 6.99996L6.99984 2.91663L11.0832 6.99996"
                      stroke="black"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span
                  className={`customSortUpLabel sorttt asc${
                    params.column.sort === "asc" ? "ascactive" : ""
                  }`}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 2.91671L7 11.0834"
                      stroke="black"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11.0835 7.00004L7.00016 11.0834L2.91683 7.00004"
                      stroke="black"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        );
      }

      if (rest.columnFilter) {
        fillterjsx = (
          <div class="customHeaderMenuButton">
            <svg
              width="16"
              height="44"
              viewBox="0 0 16 44"
              xmlns="http://www.w3.org/2000/svg"
              class="filter-svg"
              ref={(menuButton) => {
                menuButton = menuButton;
              }}
              className="customHeaderMenuButton"
              onClick={() => onMenuClicked(params, menuButton)}
            >
              <path
                d="M1 18H15"
                stroke="gray"
                stroke-width="1.33"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="gray"
              ></path>
              <path
                d="M3.5 22H12.5"
                stroke="gray"
                stroke-width="1.33"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="gray"
              ></path>
              <path
                d="M6.5 26H9.5"
                stroke="gray"
                stroke-width="1.33"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="gray"
              ></path>
            </svg>
          </div>
        );
      }
      return (
        <div className="column_header_custom">
          <span className="hidden_columns">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6"
              width={15}
              onClick={() => hiddenColumns(params)}
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
              />
            </svg>
          </span>
          <span className="ag-header-cell-text">{params.displayName}</span>{" "}
          {sort} {fillterjsx}
        </div>
      );
    };

    const isRowMaster = useCallback((dataItem) => {
      return true;

      // Check if the row has products array or messages array with data
      // const hasProducts = dataItem && dataItem.products && Array.isArray(dataItem.products) && dataItem.products.length > 0;
      // const hasMessages = dataItem && dataItem.messages && Array.isArray(dataItem.messages) && dataItem.messages.length > 0;
      // return hasProducts || hasMessages;
    }, []);

    // Custom detail cell renderer for multiple child tables
    const CustomDetailCellRenderer = (props) => {
      const [columnsData, setColumnsData] = useState(null);
      const [isLoading, setIsLoading] = useState(true);
      const [data, setData] = useState(null);

      // Use the memoized columns data
      useEffect(() => {
        const getDetailsColumns = async () => {
          try {
            const data = await detailColumns;
            setColumnsData(data);
          } catch (error) {
            setColumnsData({
              productColumns: [],
              messagesColumns: [],
              chatColumns: [],
            });
          }
        };

        getDetailsColumns();
        fetchDetailData();
      }, []);

      const fetchDetailData = async () => {
        setIsLoading(true);
        const mauticURL = process.env.REACT_APP_CRM_ADMIN;

        const response = await fetch(
          mauticURL + "/api/crm/contact/id/" + props.data.mautic_id,
          {
            method: "POST",
            body: JSON.stringify({
              additional_info: ["products", "ask_sales", "live_chats"],
            }),
          },
        );

        const findresponse = await response.json();
        if (findresponse && findresponse.code === 200) {
          if (findresponse.data) {
            setData({
              products: findresponse.data.products || [],
              messages: findresponse.data.messages || [],
              chats: findresponse.data.chats || [],
            });
          }
        }
        setIsLoading(false);
      };

      const productsCount =
        data && data.products && Array.isArray(data.products)
          ? data.products.length
          : 0;
      const messagesCount =
        data && data.messages && Array.isArray(data.messages)
          ? data.messages.length
          : 0;
      const chatCount =
        data && data.chats && Array.isArray(data.chats) ? data.chats.length : 0;

      const [activeTab, setActiveTab] = useState(null);

      if (productsCount > 0 && activeTab === null) {
        setActiveTab("products");
      } else if (messagesCount > 0 && activeTab === null) {
        setActiveTab("messages");
      } else if (chatCount > 0 && activeTab === null) {
        setActiveTab("chats");
      }

      if (!columnsData || isLoading) {
        return (
          <div
            style={{
              padding: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "16px",
            }}
          >
            Loading...
          </div>
        );
      }

      if (activeTab === null) {
        return (
          <div
            style={{
              padding: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "16px",
            }}
          >
            No Data Available
          </div>
        );
      }

      return (
        <div
          className="testinf"
          style={{ padding: "10px", background: "#f8f9fa" }}
        >
          {/* Tab Navigation */}
          <div
            style={{
              borderBottom: "1px solid #dee2e6",
              marginBottom: "10px",
              display: "flex",
              gap: "10px",
            }}
          >
            {productsCount > 0 && (
              <button
                onClick={() => setActiveTab("products")}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  background:
                    activeTab === "products" ? "#007bff" : "transparent",
                  color: activeTab === "products" ? "white" : "#007bff",
                  cursor: "pointer",
                  borderRadius: "4px 4px 0 0",
                  fontWeight: activeTab === "products" ? "bold" : "normal",
                }}
              >
                Products ({productsCount || 0})
              </button>
            )}
            {messagesCount > 0 && (
              <button
                onClick={() => setActiveTab("messages")}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  background:
                    activeTab === "messages" ? "#007bff" : "transparent",
                  color: activeTab === "messages" ? "white" : "#007bff",
                  cursor: "pointer",
                  borderRadius: "4px 4px 0 0",
                  fontWeight: activeTab === "messages" ? "bold" : "normal",
                }}
              >
                Messages ({messagesCount || 0})
              </button>
            )}
            {chatCount > 0 && (
              <button
                onClick={() => setActiveTab("chats")}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  background: activeTab === "chats" ? "#007bff" : "transparent",
                  color: activeTab === "chats" ? "white" : "#007bff",
                  cursor: "pointer",
                  borderRadius: "4px 4px 0 0",
                  fontWeight: activeTab === "chats" ? "bold" : "normal",
                }}
              >
                Live Chats ({chatCount || 0})
              </button>
            )}
          </div>

          {/* Products Tab Content */}
          {activeTab === "products" && (
            <div>
              <AgGridReact
                columnDefs={columnsData.productColumns}
                rowData={data.products || []}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                }}
                rowHeight={40}
                suppressHorizontalScroll={false}
                domLayout="autoHeight"
                theme="legacy"
                suppressContextMenu={true}
                enableCellTextSelection={false}
                enableClipboard={true}
                copyHeadersToClipboard={false}
                enableRangeSelection={true}
              />
            </div>
          )}

          {/* Messages Tab Content */}
          {activeTab === "messages" && (
            <div>
              <AgGridReact
                columnDefs={columnsData.messagesColumns}
                rowData={data.messages || []}
                defaultColDef={{
                  resizable: true,
                  sortable: false,
                }}
                rowHeight={40}
                suppressHorizontalScroll={false}
                domLayout="autoHeight"
                theme="legacy"
                suppressContextMenu={true}
                enableCellTextSelection={false}
                enableClipboard={true}
                copyHeadersToClipboard={false}
                enableRangeSelection={true}
              />
            </div>
          )}

          {/* Chats Tab Content */}
          {activeTab === "chats" && (
            <div>
              <AgGridReact
                columnDefs={columnsData.chatColumns}
                rowData={data.chats || []}
                defaultColDef={{
                  resizable: true,
                  sortable: false,
                }}
                rowHeight={40}
                suppressHorizontalScroll={false}
                domLayout="autoHeight"
                theme="legacy"
                suppressContextMenu={true}
                enableCellTextSelection={false}
                enableClipboard={true}
                copyHeadersToClipboard={false}
                enableRangeSelection={true}
              />
            </div>
          )}
        </div>
      );
    };

    const onMenuClicked = (params, event) => {
      const classes = params.attribute_set_key;
      const check = document.querySelector(`[col-id="${params.column.colId}"]`);
      params.showColumnMenu(check);
    };

    useEffect(() => {
      if (tooltipData) {
        document.addEventListener("click", handleClickOutside);
      } else {
        document.removeEventListener("click", handleClickOutside);
      }
      return () => document.removeEventListener("click", handleClickOutside);
    }, [tooltipData]);

    // Add expand/collapse column for master-detail
    const expandColumn = {
      headerName: "",
      field: "expand",
      width: 50,
      pinned: "left",
      suppressMovable: true,
      lockPosition: true,
      cellRenderer: (params) => {
        // Add null checks for params.data
        if (!params.data) {
          return null;
        }

        // Use AG Grid's node expansion state
        const isExpanded = params.node && params.node.expanded;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
              fontSize: "14px",
              paddingTop: "10px",
            }}
            onClick={() => {
              toggleRowExpansion(params.data.customer_id, params.node);
            }}
          >
            <svg
              style={{
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
              }}
              width="18px"
              height="18px"
              viewBox="0 0 21 32"
              fill="currentColor"
              aria-hidden="true"
              focusable="false"
              class="rs-picker-caret-icon rs-icon rs-icon"
              aria-label="angle down"
              data-category="legacy"
            >
              <path d="M19.196 13.143a.612.612 0 01-.179.411l-8.321 8.321c-.107.107-.268.179-.411.179s-.304-.071-.411-.179l-8.321-8.321c-.107-.107-.179-.268-.179-.411s.071-.304.179-.411l.893-.893a.582.582 0 01.411-.179c.143 0 .304.071.411.179l7.018 7.018 7.018-7.018c.107-.107.268-.179.411-.179s.304.071.411.179l.893.893a.617.617 0 01.179.411z"></path>
            </svg>
          </div>
        );
      },
    };

    function safeRenderer(fn) {
      return function (params) {
        try {
          var result = fn(params);
          return result !== undefined && result !== null
            ? result
            : React.createElement("div", null, "-NA-");
        } catch (err) {
          console.error("CellRenderer error:", err, params);
          return React.createElement("div", null, "-NA-");
        }
      };
    }

    const getCellRenderer = (fieldName, attributeType) => {
      if (fieldName === "company_name" || fieldName === "user_name") {
        return (params) => {
          let detailsUrl;
          if (rest.isLive) {
            if (params.data.product_key == "49F37046FCD84520895FC18A4F5BFC8B") {
              detailsUrl = `/customer/prod/${params.data["customer_id"]}`;
            } else {
              detailsUrl = `/customer/prodc/${params.data["customer_id"]}`;
            }
          } else if (rest.isLeadsDetailsPage) {
            detailsUrl = `/contact/${params.data["mautic_id"]}`;
          } else {
            detailsUrl = `/account/${params.data["customer_id"]}`;
          }

          let companyName = params.value ? params.value : params.data.name;

          if (!companyName) {
            const email = (params.data ? params.data.email : "") || "";
            companyName = email.substring(0, email.indexOf("@"));
            if (!companyName) {
              companyName = "Unknown";
            }
          }

          const logo = `https://ui-avatars.com/api/?name=${companyName}&color=ff0000&size=40&background=random`;

          return (
            <div style={{ alignItems: "center" }}>
              <img
                src={`${logo}`}
                className="user-image rounded"
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  marginRight: "5px",
                  objectFit: "contain",
                }}
                alt="logo"
              />{" "}
              <NavLink
                to={{
                  pathname: detailsUrl,
                }}
              >
                {companyName}
              </NavLink>
            </div>
          );
        };
      } else if (attributeType === "toggle") {
        return (params) => {
          const yesValues = [true, "true", 1, "1", "yes"];
          const value = params.value;
          let output = "-NA-";

          if (
            yesValues.includes(value) ||
            yesValues.includes(String(value).toLowerCase())
          ) {
            output = "Yes";
          } else {
            output = "No";
          }

          return <div>{output}</div>;
        };
      }
      if (attributeType === "api") {
        return safeRenderer(function (params) {
          var field = params.colDef.field;
          var data = params.data || {};

          /* ---------- ASSOCIATED PRODUCT SPECIAL CASE ---------- */
          if (field === "associated_products") {
            var values = params.value || [];
            var options = params.colDef.options || [];
            var accountTypes = data.product_account_type || [];

            if (
              Object.prototype.toString.call(values) === "[object Array]" &&
              values.length
            ) {
              var rendered = values.map(function (val) {
                if (!val) return null;

                /* ---- find label from options (THIS WAS MISSING) ---- */
                var found = null;
                for (var j = 0; j < options.length; j++) {
                  if (
                    String(options[j].id).toLowerCase() ===
                      String(val).toLowerCase() ||
                    String(options[j].value).toLowerCase() ===
                      String(val).toLowerCase()
                  ) {
                    found = options[j];
                    break;
                  }
                }

                if (!found) return null;

                var label = found.option_name || found.name || found.value;

                /* ---- find account type ---- */
                var accountType = null;
                for (var i = 0; i < accountTypes.length; i++) {
                  var parts = accountTypes[i].split(":");
                  if (parts[0] === val) {
                    accountType = parts[1];
                    break;
                  }
                }

                /* ---- color mapping ---- */
                var colorMap = {
                  paying: "green",
                  trial: "blue",
                  demo: "orange",
                  cancelled: "red",
                };

                var color = colorMap[accountType] || "#475569";

                return React.createElement(
                  "span",
                  {
                    key: val,
                    style: {
                      color: color,
                      fontWeight: 500,
                      marginRight: "6px",
                    },
                  },
                  label,
                );
              });

              return React.createElement("div", null, rendered);
            }

            return React.createElement("div", null, "-NA-");
          }

          /* ---------- EXISTING LOGIC ---------- */
          if (field === "applications") {
            var apps = params.value || data.applications;
            if (
              Object.prototype.toString.call(apps) === "[object Array]" &&
              apps.length
            ) {
              return React.createElement("div", null, apps.join(", "));
            }
            return React.createElement("div", null, "-NA-");
          }

          var options = params.colDef.options || [];
          var values = [];

          if (params.value) {
            values =
              Object.prototype.toString.call(params.value) === "[object Array]"
                ? params.value
                : [params.value];
          }

          var labels = [];

          for (var i = 0; i < values.length; i++) {
            var val = values[i];
            if (!val) continue;

            var found = null;
            for (var j = 0; j < options.length; j++) {
              if (
                String(options[j].id).toLowerCase() ===
                  String(val).toLowerCase() ||
                String(options[j].value).toLowerCase() ===
                  String(val).toLowerCase()
              ) {
                found = options[j];
                break;
              }
            }

            if (found) {
              labels.push(found.option_name || found.name || found.value);
            }
          }

          return React.createElement(
            "div",
            null,
            labels.length ? labels.join(", ") : "-NA-",
          );
        });
      }
      if (fieldName === "milestone_id") {
        return safeRenderer(function (params) {
          var detailsUrl = "#";
          var data = params.data || {};
          detailsUrl = "/deal/" + data.deal_id;

          return <NavLink to={detailsUrl}>{params.value}</NavLink>;
        });
      }
      /* ================= DATE ================= */
      if (attributeType === "date") {
        return safeRenderer(function (params) {
          if (!params.value) return "-NA-";

          const value = params.value;
          if (fieldName === "milestone_due") {
            const formattedDate = new Date(value * 1000).toLocaleDateString(
              "en-US",
              {
                timeZone: "Asia/Kolkata",
                month: "short",
                day: "2-digit",
                year: "numeric",
              },
            );
            return React.createElement("div", null, formattedDate);
          }
          let dateObj;

          if (!isNaN(Number(value))) {
            const num = Number(value);
            const ms = num > 1e12 ? num : num * 1000;
            dateObj = new Date(ms);
          } else {
            dateObj = new Date(value);
          }

          if (isNaN(dateObj.getTime())) return "-NA-";

          const formatted = format(dateObj, "MMM d, yyyy h:mm a 'IST'");

          return React.createElement("div", null, formatted);
        });
      }

      if (attributeType === "action") {
        return function ActionCellRenderer(params) {
          if (attribute_set_key === "deals") {
            return (
              <DealsActionColumn
                setActionColData={setActionColData}
                value={params.data}
              />
            );
          }

          if (attribute_set_key === "contacts") {
            return (
              <ContactsActionColumn
                setActionColData={setActionColData}
                value={params.data}
              />
            );
          }

          return null; // always safe fallback
        };
      }

      /* ================= DEFAULT ================= */
      return safeRenderer(function (params) {
        var field = params.colDef.field;

        if (
          params.colDef.field === "email" ||
          params.colDef.field === "phone_number"
        ) {
          const maskedValue = maskEmail(params.value, 4, params.colDef.field);
          return <div>{maskedValue || "-NA-"}</div>;
        }

        if (field === "milestone_amount") {
          return "$ " + (params.value ? params.value : "0");
        }
        if (field === "deal_status") {
          return (
            <p
              className={`${params.value ? "bagde-customer" : "badge-close"}`}
              style={{ marginTop: "13px" }}
            >
              {params.value ? "Open" : "Closed"}
            </p>
          );
        }
        if (field === "milestone_status") {
          const statusClassMap = {
            Draft: "badge-yellow",
            Paid: "bagde-customer",
            Lost: "bagde-internal",
            Overdue: "bagde-danger",
          };
          const currentTime = Date.now();

          const dueStatus = ["Paid", "Lost", "Draft"].includes(params.value)
            ? params.value
            : params.value === "Due"
              ? currentTime >
                new Date(params.data.milestone_due).getTime() * 1000
                ? "Overdue"
                : "Due"
              : params.value;

          return (
            <p
              className={statusClassMap[dueStatus] || "badge-close"}
              style={{ marginTop: "13px" }}
            >
              {dueStatus || "-NA-"}
            </p>
          );
        }

        if (field === "domain") {
          var domain = params.data ? params.data[field] : null;
          if (domain && domain !== "NA") {
            return React.createElement(
              "a",
              { href: "//" + domain, target: "_blank", rel: "noreferrer" },
              domain,
            );
          }
          return React.createElement("div", null, domain || "-NA-");
        }

        if (field === "lead_source") {
          var map = {
            free_trial: "Free Trial",
            live_chat: "Live Chat",
            ask_sales: "Ask Sales",
            manual: "Manual",
          };
          return React.createElement(
            "div",
            null,
            map[params.value] || params.value || "-NA-",
          );
        }

        if (Object.prototype.toString.call(params.value) === "[object Array]") {
          return React.createElement(
            "div",
            null,
            params.value.length ? params.value.join(", ") : "-NA-",
          );
        }

        return React.createElement("div", null, params.value || "-NA-");
      });
    };

    const selectionColumn = {
      headerName: "",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 80,
      pinned: "left",
      suppressMenu: true,
      sortable: false,
      filter: false,
    };

    const rsuiteSelectionColumn = {
      headerName: "",
      field: "__selection__",
      width: 55,
      pinned: "left",
      suppressMovable: true,
      lockPosition: true,
      sortable: false,
      filter: false,
      headerClass: "checkboxcol",
      cellClass: "checkboxcol",

      headerComponent: function (params) {
        var api = params.api;

        function getSelectionState() {
          var pageSize = api.paginationGetPageSize();
          var currentPage = api.paginationGetCurrentPage();
          var startRow = currentPage * pageSize;
          var endRow = startRow + pageSize - 1;

          var selected = 0;
          var totalInPage = 0;

          api.forEachNode(function (node) {
            if (node.rowIndex >= startRow && node.rowIndex <= endRow) {
              totalInPage++;
              if (node.isSelected()) {
                selected++;
              }
            }
          });

          return {
            checked: totalInPage > 0 && selected === totalInPage,
            indeterminate: selected > 0 && selected < totalInPage,
          };
        }

        var state = getSelectionState();

        return (
          <Checkbox
            checked={state.checked}
            indeterminate={state.indeterminate}
            onChange={function (value, isChecked) {
              var pageSize = api.paginationGetPageSize();
              var currentPage = api.paginationGetCurrentPage();
              var startRow = currentPage * pageSize;
              var endRow = startRow + pageSize - 1;

              api.forEachNode(function (node) {
                if (node.rowIndex >= startRow && node.rowIndex <= endRow) {
                  node.setSelected(isChecked, false, true);
                }
              });

              // Trigger selection change manually to update parent and refresh header
              onSelectionChanged();
            }}
          />
        );
      },

      cellRenderer: function (params) {
        if (!params.node || !params.data) return null;

        return (
          <Checkbox
            checked={params.node.isSelected()}
            onChange={function (value, checked) {
              params.node.setSelected(checked);
            }}
          />
        );
      },
    };

    let newcolumns = [
      ...(selection ? [rsuiteSelectionColumn] : []),
      // ...(hasChildRows ? [expandColumn] : []),
      ...columnsDef
        .filter((col) => !col.hide_col)
        .map((it) => {
          let newitem = {
            ...it,
            resizable: true,
            editable: false,
            headerComponent: (params) => customerHeaderCall(params),
            cellRenderer: getCellRenderer(it.field, it.attribute_type),
          };

          if (it.field === "company_name") {
            newitem = {
              ...newitem,
              filter: CustomFilter,
            };
          } else if (it.attribute_type === "date") {
            newitem = {
              ...newitem,
              sortable: true,
            };
          } else if (it.attribute_type === "action") {
            newitem = {
              ...newitem,
              headerName: "",
              minWidth: 60,
              maxWidth: 60,
              width: 60,
              filter: false,
              sortable: false,
              menuTabs: [],
            };
          } else if (
            [
              "account_manager",
              "sales_manager",
              "tse_owner",
              "deal_owner",
            ].includes(it.field)
          ) {
            newitem = {
              ...newitem,
              cellRenderer: (params) => {
                const mapping = userList;

                if (it.field === "tse_owner") {
                }
                if (Array.isArray(mapping) && mapping.length) {
                  if (!params.value) return "-NA-";

                  const foundItem = mapping.find(
                    (item) => item.user_uuid === params.value,
                  );
                  return foundItem ? foundItem.name || "-NA-" : "-NA-";
                }
                return "-NA-";
              },
            };
          } else {
            newitem = {
              ...newitem,
              filter: CustomFilter,
              filterParams: {
                filter: getFilterParams,
              },
              menuTabs: ["filterMenuTab"],
            };
          }

          return newitem;
        }),
    ];

    // Add the pinned "Actions" column
    if (rest.action) {
      newcolumns.push({
        headerName: "",
        field: "actions",
        pinned: "right",
        suppressMovable: true,
        width: 10,
        cellRenderer: (params) => {
          const { data } = params;
          if (data && data.customer_type !== "deleted") {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  cursor: "pointer",
                  alignItems: "center",
                  height: "100%",
                }}
                onClick={(e) => handleIconClick(e, params)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.99992 8.66668C8.36811 8.66668 8.66659 8.3682 8.66659 8.00001C8.66659 7.63182 8.36811 7.33334 7.99992 7.33334C7.63173 7.33334 7.33325 7.63182 7.33325 8.00001C7.33325 8.3682 7.63173 8.66668 7.99992 8.66668Z"
                    fill="#373945"
                    stroke="#373945"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                  <path
                    d="M7.99992 3.99999C8.36811 3.99999 8.66659 3.70151 8.66659 3.33332C8.66659 2.96513 8.36811 2.66666 7.99992 2.66666C7.63173 2.66666 7.33325 2.96513 7.33325 3.33332C7.33325 3.70151 7.63173 3.99999 7.99992 3.99999Z"
                    fill="#373945"
                    stroke="#373945"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                  <path
                    d="M7.99992 13.3333C8.36811 13.3333 8.66659 13.0349 8.66659 12.6667C8.66659 12.2985 8.36811 12 7.99992 12C7.63173 12 7.33325 12.2985 7.33325 12.6667C7.33325 13.0349 7.63173 13.3333 7.99992 13.3333Z"
                    fill="#373945"
                    stroke="#373945"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </div>
            );
          }
          return null;
        },
      });
    }

    return (
      <React.Fragment>
        {/* Add CSS to ensure master-detail arrows are visible */}
        <style jsx>{`
          .ag-theme-alpine .ag-group-expanded,
          .ag-theme-alpine .ag-group-contracted {
            display: none !important;
          }
          .ag-theme-alpine .ag-group-child-count {
            display: none;
          }
          .ag-theme-alpine .ag-row-group-leaf-indent {
            width: 0px !important;
          }
          .ag-theme-alpine .ag-row-group-indent {
            width: 0px !important;
          }
          .ag-body-viewport::-webkit-scrollbar {
            width: 6px;
          }
          .ag-body-viewport::-webkit-scrollbar-thumb {
            background-color: #888; /* Color of the scrollbar thumb */
            border-radius: 6px; /* Rounded corners for the thumb */
          }

          .ag-body-viewport::-webkit-scrollbar-track {
            background-color: #363946; /* Color of the scrollbar track */
          }

          .ag-body-viewport::-webkit-scrollbar-thumb:hover {
            background-color: #555;
          }
          .ag-theme-alpine .ag-selection-checkbox span {
            display: flex !important;
          }
          .ag-theme-alpine .ag-selection-checkbox {
            padding-left: 10px !important;
            padding-right: 10px !important;
          }
          .checkboxcol {
            padding-left: 10px !important;
            padding-right: 10px !important;
          }
          // For Horizontal Scrollbar
          .ag-body-horizontal-scroll-viewport {
            height: 10px !important;
            max-height: 3px !important;
            min-height: 10px !important;
            cursor: pointer;
          }
          .ag-body-horizontal-scroll {
            height: 16px !important;
            max-height: 3px !important;
            min-height: 11px !important;
          }
          .ag-body-horizontal-scroll-container {
            height: 9px !important;
            max-height: 3px !important;
            min-height: 9px !important;
          }
          .ag-body-horizontal-scroll {
            min-width: 0;
            width: 100%;
            display: flex !important;
            position: relative;
          }
          .ag-root ::-webkit-scrollbar-thumb,
          .list-attrs::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.4);
            border-radius: 100px;
          }
          ::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.4);
            border-radius: 10px;
          }
          ::-webkit-scrollbar-track {
            background-color: rgba(0, 0, 0, 0.1);
            border-radius: 10px;
          }
          .ag-root ::-webkit-scrollbar,
          .list-attrs::-webkit-scrollbar {
            width: 10px;
            height: 10px;
          }
          .ag-horizontal-left-spacer {
            display: none;
          }
          .ag-center-cols-viewport {
            overflow-x: hidden;
          }
          .ag-center-cols-viewport::-webkit-scrollbar,
          .ag-body-viewport.ag-layout-normal::-webkit-scrollbar {
            width: 14px !important;
          }
          .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb {
            cursor: pointer;
          }
        `}</style>
        <div
          className="ag-theme-material"
          style={{
            height: "calc(100vh - 210px)",
            width: "100%",
            padding: "0 6px",
            // Ensure master-detail arrows are visible
            "--ag-row-group-indent-size": "20px",
          }}
          tabIndex={0}
        >
          <AgGridReact
            ref={gridRef}
            columnDefs={newcolumns}
            rowModelType="serverSide"
            detailRowAutoHeight={hasChildRows}
            masterDetail={hasChildRows}
            isRowMaster={isRowMaster}
            // detailCellRenderer={CustomDetailCellRenderer}
            // detailCellRendererParams={detailCellRendererParams}
            onRowGroupOpened={(event) => {
              // Sync our expand state when AG Grid expands/collapses rows
              const customerId = event.node.data.customer_id || "";
              if (customerId) {
                setExpandedRows((prev) => {
                  const newSet = new Set(prev);
                  if (event.node.expanded) {
                    newSet.add(customerId);
                  } else {
                    newSet.delete(customerId);
                  }
                  return newSet;
                });
              }
            }}
            paginationPageSize={20}
            key={1}
            suppressContextMenu={true}
            onColumnResized={onColumnResized}
            blockLoadDebounceMillis={100}
            pagination={true}
            suppressMovableColumns={false}
            onColumnMoved={onColumnMoved}
            cacheBlockSize={20}
            enableCellTextSelection={false}
            enableClipboard={true}
            copyHeadersToClipboard={false}
            maxBlocksInCache={20}
            suppressPaginationPanel={true}
            processCellForClipboard={handleProcessCellForClipboard}
            onGridReady={onGridReady}
            statusBar={{
              statusPanels: [
                {
                  statusPanel: StatusBarPagination,
                  align: "right",
                  key: "StatusBarPagination",
                },
              ],
            }}
            serverSideStoreType="partial"
            overlayLoadingTemplate={
              '<span className="ag-overlay-loading-center">Loading...</span>'
            }
            overlayNoRowsTemplate={
              '<span className="ag-overlay-loading-center">No data found.</span>'
            }
            enableRangeSelection={true}
            rowSelection={selection ? "multiple" : undefined}
            onSelectionChanged={onSelectionChanged}
            suppressRowClickSelection={true}
            suppressAnimationFrame={true}
          />
        </div>

        {/* Tooltip Modal */}
        {rest.action && tooltipData && (
          <div
            ref={tooltipRef}
            style={{
              position: "absolute",
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              background: "#fff",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
              borderRadius: "5px",
              zIndex: 1000,
              border: "1px solid #ddd",
              width: "254px",
            }}
            className="popup-agtheme"
          >
            <CtaOptions
              params={tooltipData}
              storePermission={storePermission}
              handleClickConfirmModal={rest.handleClickConfirmModal}
              handleClickManageMuviOwnershipModal={
                rest.handleClickManageMuviOwnershipModal
              }
              handleConfirmToManageDemoAccount={
                rest.handleConfirmToManageDemoAccount
              }
              handleClickExtendFreeTrialModal={
                rest.handleClickExtendFreeTrialModal
              }
              handleConfirmManageDemoMuvi5={rest.handleConfirmManageDemoMuvi5}
              handleClickDeleteModal={rest.handleClickDeleteModal}
              handleClickSubscriptionModal={rest.handleClickSubscriptionModal}
              extendFreeTrialHandler={rest.extendFreeTrialHandler}
              handleClickConfirmModalMuviClassic={
                rest.handleClickConfirmModalMuviClassic
              }
              handleClickRestoreModal={rest.handleClickRestoreModal}
              handleClickCancelAccountModal={rest.handleClickCancelAccountModal}
              handleClickChangePasswordModal={
                rest.handleClickChangePasswordModal
              }
              handleClickExtendTrialPeriodModal={
                rest.handleClickExtendTrialPeriodModal
              }
              manageMuviOwnerShipHandler={rest.manageMuviOwnerShipHandler}
            />
          </div>
        )}
        {actionColData.openModal === "raiseCustomInvoice" && (
          <RaiseCustomInvoiceNew
            data={actionColData.raiseCustomInvoiceModalData}
            setActionColData={setActionColData}
            refetchAgGrid={refetchAgGrid}
            plist={productData}
          />
        )}
        {actionColData.openModal === "markAsPaid" && (
          <MarkAsPaidNew
            data={actionColData.markAsPaidModalData}
            // setApiRun={setApiRun}
            open={actionColData.openModal === "markAsPaid"}
            setActionColData={setActionColData}
            refetchAgGrid={refetchAgGrid}
          />
        )}
        {actionColData.openModal === "loginToAccount" && (
          <LoginToAccount
            show={true}
            data={actionColData.LoginToAccount}
            close={() =>
              setActionColData((prevState) => ({
                ...prevState,
                LoginToAccount: {},
                openModal: null,
              }))
            }
          />
        )}
      </React.Fragment>
    );
  },
);

export default memo(AgGridComponent);



// WEBPACK FOOTER //
// ./src/components/crm/leads/components/AgGridComponent.js